import { Download, FileText, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ArtefactTemplateManager } from '../components/collections/ArtefactTemplateManager'
import { AdminCollectorTemplatesSection } from '../components/velociraptor/AdminCollectorTemplatesSection'
import {
  useArtefactTemplateMutations,
  useOrgArtefactTemplates,
} from '../hooks/use-artefact-templates'
import { useIsPlatformAdmin } from '../hooks/use-auth'
import { ConfigurationCard } from './velociraptor-settings/ConfigurationCard'
import { ConnectivityTestCard } from './velociraptor-settings/ConnectivityTestCard'
import { InstallForm } from './velociraptor-settings/InstallForm'
import { PageHeader } from './velociraptor-settings/PageHeader'
import { SectionCard } from './velociraptor-settings/SectionCard'
import { ServiceStatusCard } from './velociraptor-settings/ServiceStatusCard'
import { TestCollectorCard } from './velociraptor-settings/TestCollectorCard'
import { useVelociraptorPageState } from './velociraptor-settings/use-velociraptor-page-state'

function LoadingState(): React.ReactElement {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <RefreshCw className="h-8 w-8 animate-spin text-[#4e5e82]" />
    </div>
  )
}

function ErrorBanner({ error }: { error: string | null }): React.ReactElement | null {
  if (!error) return null
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
    </div>
  )
}

interface NotConfiguredViewProps {
  page: ReturnType<typeof useVelociraptorPageState>
}

function NotConfiguredView({ page }: NotConfiguredViewProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SectionCard
      title={t('velociraptorSettings.install.title')}
      description={t('velociraptorSettings.install.description')}
      icon={Download}
    >
      <InstallForm
        onInstall={page.handleInstall}
        installing={page.settings.serviceControl.installing}
      />
    </SectionCard>
  )
}

function PlatformAdminCollectorTemplates(): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SectionCard
      title={t('velociraptorSettings.collectorTemplates.title', 'Collector Templates')}
      description={t(
        'velociraptorSettings.collectorTemplates.sectionDesc',
        'Platform-level collector templates for R&D'
      )}
      icon={FileText}
    >
      <AdminCollectorTemplatesSection />
    </SectionCard>
  )
}

function OrgTemplatesCard(): React.ReactElement {
  const { t } = useTranslation('admin')
  const orgTemplates = useOrgArtefactTemplates()
  const templateMutations = useArtefactTemplateMutations()
  return (
    <SectionCard
      title={t('velociraptorSettings.templates.title')}
      description={t('velociraptorSettings.templates.description')}
      icon={FileText}
    >
      <ArtefactTemplateManager
        templates={orgTemplates.templates}
        loading={orgTemplates.loading}
        scope="organisation"
        userRole="org_admin"
        onDelete={templateMutations.deleteOrg}
        onRefetch={() => {
          void orgTemplates.refetch()
        }}
      />
    </SectionCard>
  )
}

interface ConfiguredViewProps {
  page: ReturnType<typeof useVelociraptorPageState>
  isPlatformAdmin: boolean
}

function ConfiguredView({ page, isPlatformAdmin }: ConfiguredViewProps): React.ReactElement {
  return (
    <>
      <ServiceStatusCard settings={page.settings} />
      <ConfigurationCard config={page.settings.config} />
      <ConnectivityTestCard
        testing={page.settings.connectivity.testing}
        testResult={page.settings.connectivity.testResult}
        onTest={() => {
          void page.handleTestConnection()
        }}
      />
      {isPlatformAdmin && <PlatformAdminCollectorTemplates />}
      <TestCollectorCard
        testCollector={page.settings.testCollector}
        config={page.settings.config}
        testCollectorOs={page.testCollectorOs}
        setTestCollectorOs={page.setTestCollectorOs}
        testCollectorQuickTriage={page.testCollectorQuickTriage}
        setTestCollectorQuickTriage={page.setTestCollectorQuickTriage}
        onGenerate={() => {
          void page.handleGenerateTestCollector()
        }}
        onDownload={() => {
          void page.handleDownloadCollector()
        }}
      />
      <OrgTemplatesCard />
    </>
  )
}

export function VelociraptorSettingsPage(): React.ReactElement {
  const page = useVelociraptorPageState()
  const isPlatformAdmin = useIsPlatformAdmin()

  if (page.isLoading) {
    return <LoadingState />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        status={page.settings.status}
        isRunning={page.settings.isRunning}
        onRefresh={page.handleHeaderRefresh}
      />
      <ErrorBanner error={page.combinedError} />
      {!page.settings.isConfigured && <NotConfiguredView page={page} />}
      {page.settings.isConfigured && (
        <ConfiguredView page={page} isPlatformAdmin={isPlatformAdmin} />
      )}
    </div>
  )
}
